import { reactive, watch, nextTick } from 'vue';

// Load cart from localStorage on initialization
const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem('cart');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load cart from localStorage:', e);
  }
  return [];
};

// Load bundle discount preference from localStorage
const loadBundleDiscountPreference = () => {
  try {
    return localStorage.getItem('bundleDiscountApplied') === 'true';
  } catch (e) {
    return false;
  }
};

// Load checkout initiated flag from localStorage
const loadCheckoutInitiated = () => {
  try {
    return localStorage.getItem('checkoutInitiated') === 'true';
  } catch (e) {
    return false;
  }
};

// Load checkout initiated flag and reset cart if locked
const checkoutInitiated = loadCheckoutInitiated();
let initialItems = loadCartFromStorage();

// If cart is locked (checkout was initiated), reset it on page refresh
if (checkoutInitiated) {
  initialItems = [];
  // Clear localStorage flags
  try {
    localStorage.removeItem('cart');
    localStorage.removeItem('cartUuid');
    localStorage.removeItem('bundleDiscountApplied');
    localStorage.removeItem('checkoutInitiated');
  } catch (e) {
    console.error('Failed to clear locked cart:', e);
  }
}

// Simple reactive cart store
const cartState = reactive({
  items: initialItems,
  isOpen: false,
  bundleDiscountApplied: checkoutInitiated ? false : loadBundleDiscountPreference(),
  checkoutInitiated: false, // Always start fresh after reset
});

// Watch for changes and save to localStorage
watch(
  () => cartState.items,
  (items) => {
    try {
      localStorage.setItem('cart', JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save cart to localStorage:', e);
    }
  },
  { deep: true }
);

// Cart methods
export const cartStore = {
  state: cartState,

  addItem(product, skipSave = false) {
    // Prevent cart changes if checkout has been initiated
    if (cartState.checkoutInitiated) {
      return;
    }
    
    // Generate cart UUID if not exists
    let cartUuid = localStorage.getItem('cartUuid');
    if (!cartUuid) {
      cartUuid = 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cartUuid', cartUuid);
    }
    
    // Check if product already exists in cart
    const existingItem = cartState.items.find(item => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cartState.items.push({
        ...product,
        quantity: 1,
      });
    }
    
    // Save cart to Google Sheets (fire and forget) unless skipSave is true
    if (!skipSave) {
      this.saveCartToSheets(false);
    }
    
    // Open cart after adding item
    cartState.isOpen = true;
  },
  
  saveCartToSheets(checkedOut = false, email = null) {
    // Get block ID and page UUID from window.app
    const blockUuid = window.app?.ecommBlockId;
    const pageUuid = window.app?.ecommPageUuid;
    
    if (!blockUuid || !pageUuid || !window.axios) {
      return; // Silently fail if not configured
    }
    
    const cartUuid = localStorage.getItem('cartUuid') || 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    if (!localStorage.getItem('cartUuid')) {
      localStorage.setItem('cartUuid', cartUuid);
    }
    
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const productIds = cartState.items.map(item => item.id);
    const subtotal = cartState.items.reduce((total, item) => {
      return total + (parseFloat(item.price || 0) * item.quantity);
    }, 0);
    
    // Check if user actually qualifies for bundle discount
    const allProducts = window.app?.ecommProducts || [];
    const allProductIds = allProducts.map(p => p && p.id ? p.id : null).filter(Boolean);
    const cartProductIds = [...new Set(productIds)];
    const qualifiesForBundle = allProductIds.length >= 2 && cartProductIds.length >= allProductIds.length;
    
    // Only apply bundle discount if flag is set AND user actually qualifies
    let bundleDiscount = null;
    if (cartState.bundleDiscountApplied && qualifiesForBundle && window.app?.bundleDiscountPercent) {
      bundleDiscount = subtotal * (window.app.bundleDiscountPercent / 100);
    } else if (!qualifiesForBundle && cartState.bundleDiscountApplied) {
      // If they don't qualify but flag is set, clear the flag
      cartState.bundleDiscountApplied = false;
      try {
        localStorage.removeItem('bundleDiscountApplied');
      } catch (e) {
        console.error('Failed to remove bundle discount preference:', e);
      }
    }
    
    const total = this.getTotal();
    
    // Calculate total discount (bundle discount + any other discounts)
    const discount = bundleDiscount || 0;
    
    const url = apiUrl ? `${apiUrl}/cart/store` : '/api/cart/store';
    
    // Fire and forget - don't wait for response
    window.axios.post(url, {
      cartUuid: cartUuid,
      products: productIds || [], // Ensure products is always an array, even if empty
      cartSubtotal: subtotal,
      discount: discount,
      cartTotal: total,
      checkedOut: checkedOut,
      discountOffered: checkedOut ? 25 : null,
      bundleDiscount: bundleDiscount,
      email: email,
      blockUuid: blockUuid,
      pageUuid: pageUuid,
    }).catch(error => {
      // Silently fail - don't interrupt the cart flow
      console.error('Failed to save cart to Google Sheets:', error);
    });
  },

  removeItem(productId) {
    // Prevent cart changes if checkout has been initiated
    if (cartState.checkoutInitiated) {
      return;
    }
    
    const index = cartState.items.findIndex(item => item.id === productId);
    if (index > -1) {
      cartState.items.splice(index, 1);
      // Save cart to Google Sheets after removing item
      // Use nextTick to ensure reactive state is updated before saving
      nextTick(() => {
        this.saveCartToSheets(false);
      });
    }
  },

  updateQuantity(productId, quantity) {
    // Prevent cart changes if checkout has been initiated
    if (cartState.checkoutInitiated) {
      return;
    }
    
    const item = cartState.items.find(item => item.id === productId);
    if (item) {
      if (quantity <= 0) {
        cartStore.removeItem(productId);
      } else {
        item.quantity = quantity;
        // Save cart to Google Sheets after updating quantity
        this.saveCartToSheets(false);
      }
    }
  },

  clearCart() {
    cartState.items = [];
    cartState.bundleDiscountApplied = false;
    cartState.checkoutInitiated = false;
    // Clear cart UUID, bundle discount preference, and checkout flag when cart is cleared
    localStorage.removeItem('cartUuid');
    localStorage.removeItem('bundleDiscountApplied');
    localStorage.removeItem('checkoutInitiated');
  },
  
  initiateCheckout() {
    cartState.checkoutInitiated = true;
    // Save checkout initiated flag to localStorage
    try {
      localStorage.setItem('checkoutInitiated', 'true');
    } catch (e) {
      console.error('Failed to save checkout initiated flag:', e);
    }
  },

  toggleCart() {
    cartState.isOpen = !cartState.isOpen;
  },

  openCart() {
    cartState.isOpen = true;
  },

  closeCart() {
    cartState.isOpen = false;
  },

  getTotal() {
    const subtotal = cartState.items.reduce((total, item) => {
      return total + (parseFloat(item.price || 0) * item.quantity);
    }, 0);
    
    // Apply bundle discount if enabled
    if (cartState.bundleDiscountApplied && window.app?.bundleDiscountPercent) {
      const discount = subtotal * (window.app.bundleDiscountPercent / 100);
      return subtotal - discount;
    }
    
    return subtotal;
  },
  
  applyBundleDiscount() {
    cartState.bundleDiscountApplied = true;
    // Save bundle discount preference to localStorage
    try {
      localStorage.setItem('bundleDiscountApplied', 'true');
    } catch (e) {
      console.error('Failed to save bundle discount preference:', e);
    }
  },
  
  removeBundleDiscount() {
    cartState.bundleDiscountApplied = false;
    // Remove bundle discount preference from localStorage
    try {
      localStorage.removeItem('bundleDiscountApplied');
    } catch (e) {
      console.error('Failed to remove bundle discount preference:', e);
    }
    // Save cart to Google Sheets after removing discount
    this.saveCartToSheets(false);
  },

  getItemCount() {
    return cartState.items.reduce((count, item) => count + item.quantity, 0);
  },
};

